import type {
  ConnectorStandard,
  CurrentType,
  OperatorSlug,
  PaymentMethod,
  PointAvailability,
  StationsFeature,
  StationsFeatureProperties,
  VoltageClass,
} from '@ev/shared'
import { foldText, matchesFoldedQuery } from '../search/fold'
import { operatorKey } from '../search/untracked'
import type { FilterFacet, Filters } from './filters'

/**
 * The filter predicate. There is one, and this is it.
 *
 * The map runs it to decide what the GeoJSON source contains, the filter panel
 * reads the counts that same pass produces, and the search box restricts its
 * result list to the ids it emits. A site the panel counts, the box offers and
 * the map draws is the same site by construction, because nothing else in the
 * app is allowed to decide what "matching" means.
 *
 * It reads `stations.geojson` feature properties, because that is the only
 * artifact held in full. Two facets need data that artifact does not carry -
 * availability lives in `status.json`, payment methods live in the per-site
 * detail payloads - so they arrive through {@link StationsFilterContext}. A
 * facet whose lookup is missing is treated as unconstrained rather than as
 * matching nothing; {@link unresolvedFacets} reports which ones those are, so
 * the UI can say so out loud instead of quietly showing the wrong map.
 */

export interface StationsFilterContext {
  /** Free-text search, matched against the folded haystack below. */
  readonly query?: string
  /**
   * Site id -> its folded search haystack, from the station index.
   *
   * Folding 9 000 strings on every keystroke is the difference between a
   * responsive search box and a janky one, so it is done once when the artifact
   * lands. Absent means the index has not arrived: the query is then the only
   * facet that cannot be answered, and it fails closed rather than showing
   * everything.
   */
  readonly haystackById?: ReadonlyMap<string, string>
  /** Site id -> availability states present at that site, from `status.json`. */
  readonly availabilityBySite?: ReadonlyMap<string, ReadonlySet<PointAvailability>>
  /** Site id -> payment methods accepted there, from the site detail payloads. */
  readonly paymentMethodsBySite?: ReadonlyMap<string, ReadonlySet<PaymentMethod>>
}

/** Facets that cannot be answered from `stations.geojson` alone. */
export const CONTEXT_FACETS = [
  'availability',
  'paymentMethods',
] as const satisfies readonly FilterFacet[]

/**
 * Active facets that have no data behind them in this context, and are
 * therefore being ignored. Empty in the common case.
 */
export const unresolvedFacets = (
  filters: Filters,
  context: StationsFilterContext = {},
): readonly FilterFacet[] => {
  const missing: FilterFacet[] = []
  if (filters.availability.length > 0 && context.availabilityBySite === undefined)
    missing.push('availability')
  if (filters.paymentMethods.length > 0 && context.paymentMethodsBySite === undefined)
    missing.push('paymentMethods')
  return missing
}

/**
 * The current types a site offers, derived from its charge point counts.
 *
 * A site with neither AC nor DC points is `unknown` rather than empty, so the
 * `unknown` filter value has something to select.
 */
export const siteCurrentTypes = ({ ac, dc }: { ac: number; dc: number }): CurrentType[] => {
  const types: CurrentType[] = []
  if (ac > 0) types.push('AC')
  if (dc > 0) types.push('DC')
  return types.length === 0 ? ['unknown'] : types
}

const intersects = <T>(values: readonly T[], allowed: ReadonlySet<T>): boolean =>
  values.some((value) => allowed.has(value))

const intersectsSet = <T>(values: ReadonlySet<T>, allowed: ReadonlySet<T>): boolean => {
  let hit = false
  allowed.forEach((value) => {
    if (values.has(value)) hit = true
  })
  return hit
}

export type StationsPredicate = (properties: StationsFeatureProperties) => boolean

export interface CompileOptions {
  /**
   * Leave the operator facet out of the predicate.
   *
   * The panel needs per-operator counts computed without it, so that picking
   * Tauron does not show a zero beside every other brand and dead-end the
   * multi-select. Both halves of the facet go together: `operators` and
   * `operatorNames` answer the same question and are excluded as one.
   */
  readonly excludeOperators?: boolean
}

/**
 * "Whose charger is this?", as one test over two filter fields.
 *
 * The slug answers it for a tracked brand and the folded upstream name answers
 * it for the long tail, and they are ORed because they are one facet wearing two
 * shapes - ticking Tauron and ThreeForce has to show both, the way ticking two
 * brands does, not intersect to nothing.
 */
interface OperatorFacet {
  readonly slugs: ReadonlySet<OperatorSlug>
  readonly names: ReadonlySet<string>
}

const matchesOperator = (
  properties: StationsFeatureProperties,
  { slugs, names }: OperatorFacet,
): boolean => {
  if (slugs.has(properties.op)) return true
  if (names.size === 0 || properties.opn === undefined) return false
  return names.has(operatorKey(properties.opn))
}

/** `null` when neither half is set, which is "no constraint on the operator". */
const operatorFacet = (filters: Filters): OperatorFacet | null =>
  filters.operators.length === 0 && filters.operatorNames.length === 0
    ? null
    : {
        slugs: new Set<OperatorSlug>(filters.operators),
        names: new Set<string>(filters.operatorNames),
      }

/**
 * Build a predicate once and run it over many features.
 *
 * The facets become `Set`s and the query becomes a folded string up front, so a
 * pass over the national feature set is a handful of set lookups per site
 * rather than a rebuild of the same structures 9 000 times.
 */
export const compileStationsFilter = (
  filters: Filters,
  context: StationsFilterContext = {},
  { excludeOperators = false }: CompileOptions = {},
): StationsPredicate => {
  const operators = excludeOperators ? null : operatorFacet(filters)
  const connectors =
    filters.connectors.length > 0 ? new Set<ConnectorStandard>(filters.connectors) : null
  const currentTypes =
    filters.currentTypes.length > 0 ? new Set<CurrentType>(filters.currentTypes) : null
  const voltageClasses =
    filters.voltageClasses.length > 0 ? new Set<VoltageClass>(filters.voltageClasses) : null

  const availability =
    filters.availability.length > 0 && context.availabilityBySite !== undefined
      ? {
          allowed: new Set<PointAvailability>(filters.availability),
          bySite: context.availabilityBySite,
        }
      : null
  const paymentMethods =
    filters.paymentMethods.length > 0 && context.paymentMethodsBySite !== undefined
      ? {
          allowed: new Set<PaymentMethod>(filters.paymentMethods),
          bySite: context.paymentMethodsBySite,
        }
      : null

  const { minPowerKw } = filters
  const folded = foldText(context.query ?? '')
  const { haystackById } = context

  return (properties) => {
    // Cheapest tests first: a numeric compare and a set lookup reject most
    // sites before any array or string work happens.
    if (minPowerKw !== null && properties.pmax < minPowerKw) return false
    if (operators !== null && !matchesOperator(properties, operators)) return false
    if (currentTypes !== null && !intersects(siteCurrentTypes(properties), currentTypes))
      return false
    if (voltageClasses !== null && !intersects(properties.v, voltageClasses)) return false
    if (connectors !== null && !intersects(properties.conn, connectors)) return false

    if (availability !== null) {
      const states = availability.bySite.get(properties.id)
      if (states === undefined || !intersectsSet(states, availability.allowed)) return false
    }

    if (paymentMethods !== null) {
      const methods = paymentMethods.bySite.get(properties.id)
      if (methods === undefined || !intersectsSet(methods, paymentMethods.allowed)) return false
    }

    if (folded !== '') {
      const haystack = haystackById?.get(properties.id)
      if (haystack === undefined || !matchesFoldedQuery(haystack, folded)) return false
    }

    return true
  }
}

/**
 * Does one site pass the current filters?
 *
 * The convenience form, for a single check. Filtering a collection should use
 * {@link tallyStations}, which builds the lookup structures once.
 */
export const matchesFilters = (
  properties: StationsFeatureProperties,
  filters: Filters,
  context: StationsFilterContext = {},
): boolean => compileStationsFilter(filters, context)(properties)

export const filterStations = (
  features: readonly StationsFeature[],
  filters: Filters,
  context: StationsFilterContext = {},
): StationsFeature[] => {
  const predicate = compileStationsFilter(filters, context)
  return features.filter((feature) => predicate(feature.properties))
}

/** Everything one pass over the national feature set can answer. */
export interface StationsTally {
  /** The matching features, in artifact order. What the map draws. */
  readonly features: readonly StationsFeature[]
  /** Their ids, for the search box's eligibility set. */
  readonly ids: readonly string[]
  /**
   * Per-operator counts computed with the operator facet excluded, so ticking
   * one brand shows what adding a second would bring rather than a column of
   * zeroes. Operators with no matches are omitted.
   */
  readonly byOperator: Readonly<Partial<Record<OperatorSlug, number>>>
  /**
   * The same, keyed by {@link operatorKey}, for the networks inside `other`.
   *
   * Counted on the same relaxed predicate as {@link byOperator}, so a row for an
   * untracked network reads exactly like a row for a brand: how many sites it
   * would add, not how many are left after it was picked.
   */
  readonly byOperatorName: Readonly<Record<string, number>>
}

export const EMPTY_TALLY: StationsTally = {
  features: [],
  ids: [],
  byOperator: {},
  byOperatorName: {},
}

/**
 * Filter and count in a single pass.
 *
 * The operator-excluded predicate is a superset of the full one, so both
 * answers fall out of one walk: everything passing the relaxed predicate feeds
 * the per-operator tally, and the subset that also satisfies the operator facet
 * is the match set itself. That is one traversal of 9 000 features per filter
 * change, not three.
 */
export const tallyStations = (
  features: readonly StationsFeature[],
  filters: Filters,
  context: StationsFilterContext = {},
): StationsTally => {
  const withoutOperators = compileStationsFilter(filters, context, { excludeOperators: true })
  const selected = operatorFacet(filters)

  const matching: StationsFeature[] = []
  const ids: string[] = []
  const byOperator: Partial<Record<OperatorSlug, number>> = {}
  const byOperatorName: Record<string, number> = {}

  features.forEach((feature) => {
    const { properties } = feature
    if (!withoutOperators(properties)) return

    byOperator[properties.op] = (byOperator[properties.op] ?? 0) + 1
    if (properties.opn !== undefined) {
      const key = operatorKey(properties.opn)
      if (key !== '') byOperatorName[key] = (byOperatorName[key] ?? 0) + 1
    }

    if (selected !== null && !matchesOperator(properties, selected)) return
    matching.push(feature)
    ids.push(properties.id)
  })

  return { features: matching, ids, byOperator, byOperatorName }
}

export const countMatchingStations = (
  features: readonly StationsFeature[],
  filters: Filters,
  context: StationsFilterContext = {},
): number => {
  const predicate = compileStationsFilter(filters, context)
  let count = 0
  features.forEach((feature) => {
    if (predicate(feature.properties)) count += 1
  })
  return count
}
